import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Play, Volume2, VolumeX } from "lucide-react";

const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_cascante-moto/artifacts/cmhh62s7_WhatsApp%20Video%202026-05-12%20at%2011.55.04%20%281%29.mp4";

export default function VideoSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => {
    if (!ref.current) return;
    ref.current.muted = !ref.current.muted;
    setMuted(ref.current.muted);
  };

  const togglePlay = () => {
    if (!ref.current) return;
    if (ref.current.paused) {
      ref.current.play();
      setPlaying(true);
    } else {
      ref.current.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      data-testid="video-section"
      className="relative overflow-hidden bg-brand-black py-16 md:py-24"
    >
      {/* glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-brand-orange/25 blur-[120px]"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand-orange">
              {t("video.eyebrow", "Riding · San Juan del Sur")}
            </span>
            <h2 className="mt-2 font-display text-4xl uppercase leading-none text-white sm:text-5xl lg:text-6xl">
              {t("video.title", "Watch The Ride")}
            </h2>
          </div>
          <p className="max-w-md text-sm text-white/70">
            {t(
              "video.body",
              "From dirt trails to hidden beaches — this is what your next adventure looks like with Cascante Moto Rental."
            )}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative border-2 border-brand-orange hard-shadow-orange"
        >
          <div className="relative aspect-video w-full overflow-hidden bg-black">
            <video
              ref={ref}
              data-testid="hero-video"
              src={VIDEO_URL}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
            {/* corner brand badge */}
            <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 border border-white/20 bg-black/50 px-3 py-1.5 backdrop-blur">
              <span className="block h-2 w-2 animate-pulse rounded-full bg-brand-orange" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white">
                LIVE FROM SJDS
              </span>
            </div>

            {/* controls */}
            <div className="absolute bottom-4 right-4 flex gap-2">
              <button
                data-testid="video-play-toggle"
                onClick={togglePlay}
                className="flex h-11 w-11 items-center justify-center border border-white/20 bg-black/60 text-white backdrop-blur hover:bg-brand-orange hover:text-white"
                aria-label="Play/Pause"
              >
                <Play className={`h-5 w-5 ${playing ? "opacity-50" : ""}`} />
              </button>
              <button
                data-testid="video-mute-toggle"
                onClick={toggleMute}
                className="flex h-11 w-11 items-center justify-center border border-white/20 bg-black/60 text-white backdrop-blur hover:bg-brand-orange hover:text-white"
                aria-label="Mute/Unmute"
              >
                {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
